import { DialogComponent, DialogCloseEvent } from '@theme/dialog';
import { ThemeEvents } from '@theme/events';

/**
 * @typedef {object} TermsModalRefs
 * @property {HTMLDialogElement} dialog
 * @property {HTMLElement} titleEl
 * @property {HTMLElement} bodyEl
 * @property {HTMLElement} noticeEl
 */

/**
 * @typedef {object} TermsModalVariantConfig
 * @property {boolean} requiresApproval
 * @property {boolean} accepted
 * @property {string} title
 * @property {string} body
 * @property {string} notice
 */

/**
 * Gates add-to-cart / accelerated checkout for variants that require terms
 * approval, showing a dialog before the original action is allowed through.
 * Approval is persisted per-variant as a cart attribute.
 *
 * Interception happens on the `click` event (capture phase, on `window`)
 * rather than the form's `submit` event: some cart/upsell apps rewrite the
 * add-to-cart button's `type` from `submit` to `button` and drive the add
 * themselves via their own click handler, so no native `submit` event ever
 * fires. Capturing at `window` runs before any bubble-phase listener
 * (Horizon's own delegated `on:click`, or a third-party app's), so this wins
 * the race regardless of what else is wired to the button. A `submit`
 * listener is kept as a fallback for submissions that don't originate from
 * a click on the button (e.g. pressing Enter in a form field).
 * @extends DialogComponent<TermsModalRefs>
 */
class TermsModalComponent extends DialogComponent {
  requiredRefs = ['dialog', 'titleEl', 'bodyEl', 'noticeEl'];

  /** @type {Record<string, TermsModalVariantConfig>} */
  #variants = {};

  /** @type {string} */
  #cartAttributePrefix = 'terms_accepted_';

  /** @type {HTMLFormElement | null} */
  #pendingForm = null;

  /** @type {(() => void) | null} */
  #pendingResume = null;

  /** @type {boolean} */
  #bypassNextClick = false;

  connectedCallback() {
    super.connectedCallback();

    const config = this.#readConfig();
    if (!config) return;

    this.#variants = config.variants;
    this.#cartAttributePrefix = config.cartAttributePrefix;

    window.addEventListener('click', this.#onWindowClick, { capture: true });
    window.addEventListener('submit', this.#onWindowSubmit, { capture: true });
    document.addEventListener(ThemeEvents.variantUpdate, this.#onVariantUpdate);
    this.addEventListener(DialogCloseEvent.eventName, this.#onDialogClose);

    this.#toggleAcceleratedCheckout(this.#getSelectedVariantId());
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    window.removeEventListener('click', this.#onWindowClick, { capture: true });
    window.removeEventListener('submit', this.#onWindowSubmit, { capture: true });
    document.removeEventListener(ThemeEvents.variantUpdate, this.#onVariantUpdate);
  }

  /** @returns {{ cartAttributePrefix: string, variants: Record<string, TermsModalVariantConfig> } | null} */
  #readConfig() {
    const script = /** @type {HTMLScriptElement | null} */ (this.querySelector('script[data-terms-modal-config]'));
    if (!script?.textContent) return null;

    try {
      return JSON.parse(script.textContent);
    } catch (error) {
      console.error('terms-modal-component: invalid config', error);
      return null;
    }
  }

  /** @returns {Element | null} */
  #getProductFormComponent() {
    return document.querySelector(`product-form-component[data-product-id="${this.dataset.productId}"]`);
  }

  /** @returns {HTMLFormElement | null} */
  #getProductForm() {
    return this.#getProductFormComponent()?.querySelector('form') ?? null;
  }

  /** @returns {string | undefined} */
  #getSelectedVariantId() {
    return this.#getFormVariantId(this.#getProductForm());
  }

  /** @param {HTMLFormElement | null} form @returns {string | undefined} */
  #getFormVariantId(form) {
    const input = /** @type {HTMLInputElement | null | undefined} */ (form?.querySelector('[name="id"]'));
    return input?.value || undefined;
  }

  /**
   * @param {Element} productFormEl
   * @returns {TermsModalVariantConfig | undefined}
   */
  #getPendingVariantConfig(productFormEl) {
    if (productFormEl.getAttribute('data-product-id') !== this.dataset.productId) return undefined;

    const variantId = this.#getFormVariantId(productFormEl.querySelector('form'));
    return variantId ? this.#variants[variantId] : undefined;
  }

  /** @param {MouseEvent} event */
  #onWindowClick = (event) => {
    if (this.#bypassNextClick) {
      this.#bypassNextClick = false;
      return;
    }

    const target = event.target;
    if (!(target instanceof Element)) return;

    const addToCartButton = target.closest('[name="add"]');
    if (!addToCartButton) return;

    const productFormEl = addToCartButton.closest('product-form-component');
    if (!productFormEl) return;

    const variantConfig = this.#getPendingVariantConfig(productFormEl);
    if (!variantConfig?.requiresApproval || variantConfig.accepted) return;

    event.preventDefault();
    event.stopPropagation();

    this.#pendingForm = productFormEl.querySelector('form');
    this.#pendingResume = () => {
      this.#bypassNextClick = true;
      /** @type {HTMLElement} */ (addToCartButton).click();
    };
    this.#openDialog(variantConfig);
  };

  /** @param {SubmitEvent} event */
  #onWindowSubmit = (event) => {
    const form = event.target;
    if (!(form instanceof HTMLFormElement)) return;

    const productFormEl = form.closest('product-form-component');
    if (!productFormEl) return;

    const variantConfig = this.#getPendingVariantConfig(productFormEl);
    if (!variantConfig?.requiresApproval || variantConfig.accepted) return;

    event.preventDefault();
    event.stopPropagation();

    this.#pendingForm = form;
    this.#pendingResume = () => form.requestSubmit();
    this.#openDialog(variantConfig);
  };

  /** @param {CustomEvent} event */
  #onVariantUpdate = (event) => {
    const productId = event.detail?.data?.productId;
    if (productId != null && String(productId) !== this.dataset.productId) return;

    const variantId = event.detail?.resource?.id;
    if (variantId == null) return;

    this.#toggleAcceleratedCheckout(String(variantId));
  };

  /**
   * Hides the accelerated checkout button (Shop Pay, PayPal, etc.) while the
   * selected variant requires approval that hasn't been given yet, since
   * those buttons render inside their own embedded context and can't be
   * intercepted the way a same-page click can.
   * @param {string | undefined} variantId
   */
  #toggleAcceleratedCheckout(variantId) {
    const variantConfig = variantId ? this.#variants[variantId] : undefined;
    const needsApproval = !!variantConfig?.requiresApproval && !variantConfig.accepted;

    const acceleratedCheckout = this.#getProductForm()?.querySelector('.accelerated-checkout-block');
    if (acceleratedCheckout instanceof HTMLElement) acceleratedCheckout.hidden = needsApproval;
  }

  /** @param {TermsModalVariantConfig} variantConfig */
  #openDialog(variantConfig) {
    const { titleEl, bodyEl, noticeEl } = this.refs;
    titleEl.textContent = variantConfig.title;
    bodyEl.textContent = variantConfig.body;
    noticeEl.textContent = variantConfig.notice;
    noticeEl.hidden = !variantConfig.notice;

    this.showDialog();
  }

  handleCancel() {
    this.closeDialog();
  }

  async handleConfirm() {
    const resume = this.#pendingResume;
    if (!resume) {
      this.closeDialog();
      return;
    }

    const variantId = this.#getFormVariantId(this.#pendingForm);
    if (variantId) await this.#acceptVariant(variantId);

    this.closeDialog();
    resume();
  }

  /** @param {string} variantId */
  async #acceptVariant(variantId) {
    const variantConfig = this.#variants[variantId];
    if (variantConfig) variantConfig.accepted = true;
    this.#toggleAcceleratedCheckout(variantId);

    try {
      await fetch('/cart/update.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ attributes: { [this.#cartAttributePrefix + variantId]: 'true' } }),
        keepalive: true,
      });
    } catch (error) {
      console.error('terms-modal-component: failed to persist approval', error);
    }
  }

  #onDialogClose = () => {
    this.#pendingForm = null;
    this.#pendingResume = null;
  };
}

if (!customElements.get('terms-modal-component')) {
  customElements.define('terms-modal-component', TermsModalComponent);
}
